import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, GraduationCap, Eye, Calendar, Coins, HandHeart, Send, Building2 } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { postMessage } from "@/lib/api";
import { IMAGES } from "@/lib/site";

const TYPES = [
  { id: "stages", label: "Stages / PFMP", icon: Briefcase },
  { id: "taxe", label: "Taxe d'apprentissage", icon: Coins },
  { id: "interventions", label: "Interventions", icon: GraduationCap },
  { id: "visites", label: "Visites", icon: Eye },
  { id: "evenements", label: "Événements", icon: Calendar },
  { id: "mecenat", label: "Mécénat", icon: HandHeart },
  { id: "autre", label: "Autre", icon: Building2 },
];

const benefices = [
  { icon: Briefcase, t: "Accueil de stagiaires", d: "Découvrir et accompagner les futurs talents du territoire." },
  { icon: GraduationCap, t: "Interventions en classe", d: "Partager votre métier et inspirer la prochaine génération." },
  { icon: Eye, t: "Visites d'entreprise", d: "Ouvrir vos portes à nos élèves pour des immersions concrètes." },
  { icon: Calendar, t: "Participation à nos événements", d: "Job dating, forum métiers, soirée des partenaires…" },
  { icon: Coins, t: "Taxe d'apprentissage", d: "Soutenir directement nos équipements et projets pédagogiques." },
  { icon: HandHeart, t: "Mécénat & dons en nature", d: "Contribuer à des projets sociaux, culturels ou matériels." },
];

const initial = {
  entreprise: "",
  secteur: "",
  siret: "",
  adresseEntreprise: "",
  siteWeb: "",
  nom: "",
  fonction: "",
  email: "",
  telephone: "",
  types: [],
  message: "",
};

export default function DevenirPartenaire() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const toggleType = (id) =>
    setForm((f) => ({
      ...f,
      types: f.types.includes(id) ? f.types.filter((t) => t !== id) : [...f.types, id],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.types.length === 0) {
      toast.error("Sélectionnez au moins un type de partenariat.");
      return;
    }
    setSubmitting(true);
    const typesLabels = form.types
      .map((id) => TYPES.find((t) => t.id === id)?.label || id)
      .join(", ");
    const composedMessage =
      `── Informations entreprise ──\n` +
      `Secteur : ${form.secteur || "—"}\n` +
      `SIRET : ${form.siret || "—"}\n` +
      `Adresse : ${form.adresseEntreprise || "—"}\n` +
      `Site web : ${form.siteWeb || "—"}\n\n` +
      `── Contact ──\n` +
      `Fonction : ${form.fonction || "—"}\n\n` +
      `── Type de partenariat souhaité ──\n${typesLabels}\n\n` +
      `── Message ──\n${form.message || "—"}`;

    try {
      await postMessage({
        nom: form.nom,
        email: form.email,
        telephone: form.telephone,
        sujet: `Devenir partenaire — ${form.entreprise}`,
        message: composedMessage,
        type: "partenariat",
        entreprise: form.entreprise,
      });
      toast.success("Votre demande a bien été envoyée. Nous vous recontactons rapidement.");
      setForm(initial);
    } catch {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="devenir-partenaire-page">
      <PageHero
        eyebrow="Espace entreprises · Devenir partenaire"
        title="Rejoignez notre réseau d'entreprises engagées"
        subtitle="Le lycée Joseph Wresinski invite les acteurs économiques du territoire à co-construire la réussite professionnelle de nos jeunes. De multiples formes de collaboration sont possibles, à votre rythme et selon vos moyens."
        image={IMAGES.partenaires}
      />

      <Section eyebrow="Vos possibilités d'engagement" title="Plusieurs façons de nous rejoindre">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {benefices.map((b, i) => (
            <Card key={b.t} data-testid={`benefice-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-slate/10 text-brand-slate-dark">
                <b.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{b.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section wrapper="bg-brand-sand-wrapper/40">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:items-start">
          <div className="space-y-5">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-sand-dark">
              Formulaire de partenariat
            </p>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
              Échangeons sur votre projet de collaboration
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Renseignez ce formulaire et notre Bureau des entreprises vous recontactera sous 48h pour
              définir ensemble le meilleur cadre de partenariat.
            </p>
            <div className="rounded-2xl border border-black/5 bg-brand-cream p-5 text-sm text-muted-foreground">
              <p className="font-semibold text-brand-slate-dark">Une autre demande ?</p>
              <p className="mt-2">
                Pour la <Link to="/entreprises/taxe-apprentissage" className="text-brand-slate underline-offset-4 hover:underline">taxe d'apprentissage</Link>{" "}
                ou le <Link to="/entreprises/bureau" className="text-brand-slate underline-offset-4 hover:underline">Bureau des entreprises</Link>,
                consultez les pages dédiées.
              </p>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="devenir-form"
            className="rounded-3xl border border-black/5 bg-brand-cream p-8 shadow-sm"
          >
            {/* Entreprise */}
            <div className="border-b border-black/5 pb-6">
              <p className="font-heading text-base font-semibold text-brand-slate-dark">
                Informations entreprise
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field testid="dp-entreprise" label="Nom de l'entreprise" name="entreprise" value={form.entreprise} onChange={onChange} required />
                <Field testid="dp-secteur" label="Secteur d'activité" name="secteur" value={form.secteur} onChange={onChange} required />
                <Field testid="dp-siret" label="SIRET" name="siret" value={form.siret} onChange={onChange} />
                <Field testid="dp-siteweb" label="Site web" name="siteWeb" value={form.siteWeb} onChange={onChange} placeholder="https://…" />
                <div className="sm:col-span-2">
                  <Field testid="dp-adresse" label="Adresse" name="adresseEntreprise" value={form.adresseEntreprise} onChange={onChange} />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="border-b border-black/5 py-6">
              <p className="font-heading text-base font-semibold text-brand-slate-dark">Contact</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field testid="dp-nom" label="Nom & prénom" name="nom" value={form.nom} onChange={onChange} required />
                <Field testid="dp-fonction" label="Fonction" name="fonction" value={form.fonction} onChange={onChange} required />
                <Field testid="dp-email" label="Email" type="email" name="email" value={form.email} onChange={onChange} required />
                <Field testid="dp-tel" label="Téléphone" name="telephone" value={form.telephone} onChange={onChange} />
              </div>
            </div>

            {/* Types */}
            <div className="border-b border-black/5 py-6">
              <p className="font-heading text-base font-semibold text-brand-slate-dark">
                Type de partenariat envisagé <span className="text-brand-sand-dark">*</span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Plusieurs choix possibles</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {TYPES.map((t) => {
                  const checked = form.types.includes(t.id);
                  return (
                    <label
                      key={t.id}
                      data-testid={`dp-type-${t.id}`}
                      className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-medium transition-all ${
                        checked
                          ? "border-brand-slate bg-brand-slate text-brand-cream"
                          : "border-border bg-brand-cream text-brand-slate-dark hover:border-brand-slate/40"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={checked}
                        onChange={() => toggleType(t.id)}
                      />
                      <t.icon className="h-4 w-4" />
                      {t.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Message */}
            <div className="py-6">
              <label className="text-sm font-medium text-brand-slate-dark">
                Message libre
              </label>
              <textarea
                rows={5}
                name="message"
                value={form.message}
                onChange={onChange}
                placeholder="Présentez-nous brièvement votre projet de collaboration…"
                data-testid="dp-message"
                className="mt-2 w-full rounded-2xl border border-border bg-brand-cream px-4 py-3 text-sm outline-none transition-all focus:border-brand-slate focus:ring-2 focus:ring-brand-sage/40"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="dp-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark disabled:opacity-60 sm:w-auto"
            >
              <Send className="h-4 w-4" />
              {submitting ? "Envoi en cours…" : "Envoyer ma demande"}
            </button>
          </form>
        </div>
      </Section>
    </div>
  );
}

const Field = ({ label, name, value, onChange, type = "text", required = false, testid, placeholder }) => (
  <div>
    <label className="text-sm font-medium text-brand-slate-dark">
      {label} {required && <span className="text-brand-sand-dark">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-2 w-full rounded-2xl border border-border bg-brand-cream px-4 py-3 text-sm outline-none transition-all focus:border-brand-slate focus:ring-2 focus:ring-brand-sage/40"
    />
  </div>
);
